import React from 'react';
import { Play } from 'lucide-react';
import { Clip, TrackKind } from '../../types';
import { KIND_ICONS } from '../../../canvas/constants/object-meta';
import { colors, dimensions, typography } from '../../../theme';
import { DragMode, DragSnapshot } from '../../hooks/use-clip-drag';

type ClipBlockProps = {
  clip: Clip;
  trackKind: TrackKind;
  renderClip?: Clip;
  previewClipId?: string;
  pxPerSecond: number;
  isSelected: boolean;
  startOffset: number;
  label?: string;
  // false para clips relacionados del grupo inicial (video/texto): no muestra handles de resize.
  resizable?: boolean;
  dragSnapshot?: DragSnapshot | null;
  onClick?: (e: React.MouseEvent) => void;
  onDragStart?: (e: React.MouseEvent, mode: DragMode) => void;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick' | 'onDragStart'>;

type Geometry = { left: number; width: number };

const MIN_WIDTH_PX = 12;
const HANDLE_WIDTH = 6;
const SHOW_HANDLES_THRESHOLD = 24;

const SHOW_ICON_THRESHOLD = 26;
const SHOW_LABEL_THRESHOLD = 60;
const SHOW_WAVEFORM_THRESHOLD = 100;

const SELECTED_RING = `0 0 0 2px ${colors.accent}`;

const computeGeometry = (clip: Clip, pxPerSecond: number, startOffset: number): Geometry => ({
  left: (clip.start / 1000) * pxPerSecond + startOffset,
  width: Math.max((clip.duration / 1000) * pxPerSecond, MIN_WIDTH_PX)
});

const applyDragPreview = (g: Geometry, snapshot: DragSnapshot | null | undefined, clipId: string): Geometry => {
  if (!snapshot || snapshot.clipId !== clipId) return g;
  switch (snapshot.mode) {
    case 'move':
      return { left: g.left + snapshot.offsetPx, width: g.width };
    case 'resize-end':
      return { left: g.left, width: Math.max(MIN_WIDTH_PX, g.width + snapshot.offsetPx) };
    case 'resize-start':
      return {
        left: g.left + snapshot.offsetPx,
        width: Math.max(MIN_WIDTH_PX, g.width - snapshot.offsetPx)
      };
  }
};

// Avoid horizontal padding here: the clip element defines the visible track
// bounds and inner padding would push content past the rounded border. Use
// `gap` for icon/label spacing.
const innerBase: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: dimensions.spacing.md,
  overflow: 'hidden',
  borderRadius: dimensions.radius.xl,
  fontFamily: typography.fontFamily.ui,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.semibold,
  letterSpacing: typography.letterSpacing.wide,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  pointerEvents: 'none'
};

const labelSpanStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

type VariantProps = {
  width: number;
  label: string;
};

const IconBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      width: 18,
      height: 18,
      borderRadius: dimensions.radius.pill,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginLeft: dimensions.spacing.sm
    }}
  >
    {children}
  </span>
);

const KindIcon: React.FC<{ kind: TrackKind; size?: number }> = ({ kind, size = 11 }) => {
  const Icon = KIND_ICONS[kind];
  return <Icon size={size} strokeWidth={2.5} />;
};

const TextClip: React.FC<VariantProps> = ({ width, label }) => (
  <div style={{ ...innerBase, backgroundColor: colors.clipTextBg, color: colors.clipTextFg, textTransform: 'none' }}>
    {width > SHOW_ICON_THRESHOLD && (
      <IconBadge>
        <KindIcon kind='text' />
      </IconBadge>
    )}
    {width > SHOW_LABEL_THRESHOLD && <span style={labelSpanStyle}>{label}</span>}
  </div>
);

const SubtitleClip: React.FC<VariantProps> = ({ width, label }) => (
  <div
    style={{
      ...innerBase,
      backgroundColor: colors.clipSubtitleBg,
      color: colors.clipSubtitleFg,
      border: `1px solid ${colors.clipSubtitleBorder}`,
      fontWeight: typography.fontWeight.regular,
      textTransform: 'none',
      justifyContent: 'center'
    }}
  >
    {width > SHOW_ICON_THRESHOLD && <span style={labelSpanStyle}>{label}</span>}
  </div>
);

const VideoClip: React.FC<VariantProps> = ({ width, label }) => (
  <div style={{ ...innerBase, backgroundColor: colors.clipVideoBg, color: colors.clipVideoFg }}>
    {width > SHOW_ICON_THRESHOLD && (
      <IconBadge>
        <Play size={10} fill='currentColor' strokeWidth={0} />
      </IconBadge>
    )}
    {width > SHOW_LABEL_THRESHOLD && <span style={labelSpanStyle}>{label}</span>}
  </div>
);

const MediaClip: React.FC<VariantProps> = ({ width, label }) => (
  <div style={{ ...innerBase, backgroundColor: colors.clipMediaBg, color: colors.clipMediaFg }}>
    {width > SHOW_ICON_THRESHOLD && (
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: dimensions.radius.sm,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginLeft: dimensions.spacing.xs,
          color: colors.onAccent
        }}
      >
        <KindIcon kind='media' size={12} />
      </span>
    )}
    {width > SHOW_LABEL_THRESHOLD && <span style={labelSpanStyle}>{label}</span>}
  </div>
);

const WaveformDecoration: React.FC<{ color: string }> = ({ color }) => {
  const bars = [3, 6, 4, 8, 5, 9, 6, 4, 7, 5, 8, 4, 6, 3, 5, 7, 4, 6];
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, height: '100%', overflow: 'hidden' }}>
      {bars.map((h, i) => (
        <span
          key={i}
          style={{
            display: 'block',
            width: 2,
            height: `${h * 10}%`,
            backgroundColor: color,
            borderRadius: dimensions.radius.pill,
            flexShrink: 0,
            opacity: 0.6
          }}
        />
      ))}
    </span>
  );
};

const AudioClip: React.FC<VariantProps> = ({ width, label }) => (
  <div style={{ ...innerBase, backgroundColor: colors.clipAudioBg, color: colors.clipAudioFg }}>
    {width > SHOW_ICON_THRESHOLD && (
      <span style={{ flexShrink: 0, marginLeft: dimensions.spacing.lg, display: 'flex' }}>
        <KindIcon kind='audio' size={12} />
      </span>
    )}
    {width > SHOW_LABEL_THRESHOLD && <span style={labelSpanStyle}>{label}</span>}
    {width > SHOW_WAVEFORM_THRESHOLD && <WaveformDecoration color={colors.clipAudioFg} />}
  </div>
);

const SfxClip: React.FC<VariantProps> = () => (
  <div
    style={{
      ...innerBase,
      backgroundColor: colors.clipSfxBg,
      color: colors.clipSfxFg,
      borderRadius: dimensions.radius.pill
    }}
  />
);

const ShapeClip: React.FC<VariantProps> = ({ width, label }) => (
  <div style={{ ...innerBase, backgroundColor: colors.clipShapeBg, color: colors.clipShapeFg }}>
    {width > SHOW_ICON_THRESHOLD && (
      <IconBadge>
        <KindIcon kind='shape' />
      </IconBadge>
    )}
    {width > SHOW_LABEL_THRESHOLD && <span style={labelSpanStyle}>{label}</span>}
  </div>
);

const renderVariant = (kind: TrackKind, props: VariantProps): React.ReactNode => {
  switch (kind) {
    case 'text':
      return <TextClip {...props} />;
    case 'subtitle':
      return <SubtitleClip {...props} />;
    case 'video':
      return <VideoClip {...props} />;
    case 'media':
      return <MediaClip {...props} />;
    case 'audio':
      return <AudioClip {...props} />;
    case 'effect':
      return <SfxClip {...props} />;
    case 'shape':
      return <ShapeClip {...props} />;
  }
};

const ResizeHandle: React.FC<{
  side: 'left' | 'right';
  onMouseDown: (e: React.MouseEvent) => void;
}> = ({ side, onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      [side]: 0,
      width: HANDLE_WIDTH,
      cursor: 'ew-resize',
      zIndex: 2
    }}
  />
);

export const ClipBlock: React.FC<ClipBlockProps> = ({
  clip,
  trackKind,
  renderClip,
  previewClipId,
  pxPerSecond,
  isSelected,
  startOffset,
  label,
  resizable = true,
  dragSnapshot,
  onClick,
  onDragStart,
  ...divProps
}) => {
  const geometryClip = renderClip ?? clip;
  const baseGeo = computeGeometry(geometryClip, pxPerSecond, startOffset);
  const geo = applyDragPreview(baseGeo, dragSnapshot, previewClipId ?? clip.id);
  const displayLabel = label ?? '';
  const showHandles = !!onDragStart && resizable && geo.width > SHOW_HANDLES_THRESHOLD;

  return (
    <div
      {...divProps}
      onClick={onClick}
      onMouseDown={onDragStart ? e => onDragStart(e, 'move') : undefined}
      style={{
        position: 'absolute',
        left: geo.left,
        width: geo.width,
        top: 4,
        bottom: 4,
        cursor: onDragStart ? 'grab' : 'default',
        boxShadow: isSelected ? SELECTED_RING : 'none',
        borderRadius: dimensions.radius.xl,
        userSelect: 'none'
      }}
    >
      {renderVariant(trackKind, { width: geo.width, label: displayLabel })}
      {showHandles && (
        <>
          <ResizeHandle side='left' onMouseDown={e => onDragStart(e, 'resize-start')} />
          <ResizeHandle side='right' onMouseDown={e => onDragStart(e, 'resize-end')} />
        </>
      )}
    </div>
  );
};
